import React, {useRef, useState} from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Typography, IconButton, Tooltip } from '@material-ui/core';
import { hasPermission, hasAtLeastOnePermission, createHistory } from '../../sc-utilities';
import ExternalSystemSettingsList from './ExternalSystemSettingList';
import { FindInPage, VpnKey } from '@material-ui/icons';
import * as actions from './store/externalSystemSettingActions';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='statusActive']": {
            color: theme.palette.success.dark
        },
        "[class*='statusInactive']": {
            color: theme.palette.error.dark
        }
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    }
});

const ExternalSystemSetting = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, getBearerToken, activeCustomerID } = props;

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, externalSystemSettingID: null, redirectPath: '' });

    let visible = columns && columns['ExternalSystemSettings'];
    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: rowData => <>
                {hasPermission(permissions, 'external_system_setting_load') ?
                    <Tooltip key={`Edit-${rowData.ExternalSystemSettingID}`} title={translations["external_system_setting_details"] || "External system setting details"}>
                        <IconButton
                            id={`edit_external_system_setting_${rowData.ExternalSystemSettingID}`}
                            component='div'
                            onClick={(event) => redirectHandler(`/externalSystemSetting/edit?externalSystemSettingID=${rowData.ExternalSystemSettingID}`)}
                        >
                            <FindInPage />
                        </IconButton>
                    </Tooltip>
                    : null
                }
                {rowData.Type === 'servicenow' ?
                    <Tooltip key={`GetBearerToken`} title={translations["get_bearer_token"] || "Get Bearer Token"}>
                        <IconButton
                            id={`get_bearer_token_${rowData.ExternalSystemSettingID}`}
                            component='div'
                            onClick={(event) => handleBearerToken(event, rowData)}
                        >
                            <VpnKey />
                        </IconButton>
                    </Tooltip>
                    : null
                }
            </>
        },
        {
            title: translations["name"] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["direction"] || 'Direction', field: 'Direction',
            hidden: visible ? visible['Direction'] : false,
            filtering: true,
            lookup: { 'in': translations['in'] || 'in', 'out': translations['out'] || 'out', 'in+out': `${translations['in'] || 'in'}+${translations['out'] || 'out'}` },
            defaultFilter: ['in', 'out', 'in+out'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Direction ? rowData.Direction : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["type"] || 'Type', field: 'Type',
            hidden: visible ? visible['Type'] : false,
            filtering: true,
            lookup: { 'service': translations['service'] || 'Service', 'database': translations['database'] || 'Database', 'servicenow': translations['servicenow'] || 'ServiceNow', 'kloudless': translations['kloudless'] || 'Kloudless', 'json': translations['json'] || 'JSON', 'makor': translations['makor'] || 'Makor ERP', 'store_external_data': translations['store_external_data'] || 'Store External Data' },
            defaultFilter: ['service', 'database', 'servicenow', 'kloudless', 'json', 'makor', 'store_external_data'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Type ? rowData.Type : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["active"] || 'Active', field: 'Active',
            hidden: visible ? visible['Active'] : false,
            filtering: true,
            lookup: { '1': translations['active'] || 'Active', '0': translations['inactive'] || 'Inactive' },
            defaultFilter: ['1', '0'],
            render: rowData => {
                return <CustomChip key={`active_${rowData.ExternalSystemSettingID}`} className={rowData.Active ? 'statusActive' : 'statusInactive'} value={rowData.Active ? translations['active'] || 'Active' : translations['inactive'] || 'Inactive'} />;
            }
        }
    ];

    if (!hasAtLeastOnePermission(permissions, [
        "external_system_setting_load"
    ])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, externalSystemSettingID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, externalSystemSettingID: externalSystemSettingID, redirectPath: path })
    };

    const handleBearerToken = async (event, rowData) => {
        await getBearerToken({ ExternalSystemSettingID: rowData.ExternalSystemSettingID, ActiveCustomerID: activeCustomerID });
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasAtLeastOnePermission(permissions, ['external_system_setting_list']) ?
                <ExternalSystemSettingsList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                />
                :
                null}
        </div>
    );
};

const CustomChip = props => {
    const { keyValue, value, className } = props;

    return (
        <Typography key={keyValue} className={className} variant={'caption'}>{value}</Typography>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
    };
};

const mapDispatchToProps = dispatch => {
    return {
        getBearerToken: params => dispatch(actions.getBearerToken(params)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(ExternalSystemSetting));